import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from 'src/utils/axios';
import { isValidToken, setSession, setUserType } from 'src/utils/commonJwt';
import axiosInstance from 'src/utils/axios';
import useAuth from 'src/hooks/commonUseAuth';
import localStorageAvailable from '@utils/localStorageAvailable';
import { useRouter } from 'node_modules/next/router';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isRegistered: false,
  isInitialized: false,
  user: null,
  user_type: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, user_type } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      user_type,
    };
  },
  LOGIN: (state, action) => {
    const { user, user_type } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      user_type,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    user_type: null,
  }),
  REGISTER: (state, action) => {
    const { isRegistered } = action.payload;
    return {
      ...state,
      isRegistered,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'commonJwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  employeeLogin: () => Promise.resolve(),
  adminLogut: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

CommonAuthProvider.propTypes = {
  children: PropTypes.node,
};

function CommonAuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();
  const router = useRouter();
  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? window.localStorage.getItem('commonAccessToken') : '';
      const userType = window.localStorage.getItem('commonUserType');
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const isSuperAdmin = userType === 'user';

        await axiosInstance.get(isSuperAdmin ? '/admin/auth/profile' : '/employee/auth/profile').then((response) => {
          if (response.status === 200) {
            const { user, user_type } = response.data;
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user,
                user_type: user_type,
              },
            });
          }
        });
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize, router]);

  const login = async (email, password, enqueueSnackbar) => {
    const response = await axios.post('/admin/auth/signin', {
      email,
      password,
    });

    if (response.status === 200) {
      const { accessToken, user, message, companyEmployee, user_type } = response.data;
      enqueueSnackbar(message);
      setSession(accessToken);
      setUserType(user_type);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: companyEmployee || user,
          user_type: user_type,
        },
      });
    }
  };

  const employeeLogin = async (email, password, enqueueSnackbar) => {
    const response = await axios.post('/employee/auth/signin', {
      email,
      password,
    });

    if (response.status === 200) {
      const { accessToken, user, message, companyEmployee, user_type } = response.data;
      enqueueSnackbar(message);

      setSession(accessToken);
      setUserType(user_type);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: companyEmployee || user,
          user_type: user_type,
        },
      });
    }
  };

  const logout = async () => {
    await axiosInstance.delete('/employee/auth/signout').then((response) => {
      if (response.status === 200) {
        setSession(null);
        setUserType(null);
        dispatch({ type: 'LOGOUT' });
      }
    });
  };

  const adminLogut = async () => {
    await axiosInstance.delete('/admin/auth/signout').then((response) => {
      if (response.status === 200) {
        setSession(null);
        setUserType(null);
        dispatch({ type: 'LOGOUT' });
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'commonJwt',
        login,
        logout,
        employeeLogin,
        adminLogut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, CommonAuthProvider };
